/** @flow */

import { takeLatest, call, put } from "redux-saga/effects";
import http from "@http";
import actionCreator from "../../actionCreator";
import { transformSearchQuery, OPERATORS } from "@transformers/request";
import { sortObjToStr } from "@transformers/sort";
import type { FilesResponse, FilesRequestPayload } from "../../types";
import {
  GetDataFileArchive,
  GetDataFileArchiveSummary,
  GetFileChangesDetails,
  DownloadDataSipFile,
  DownloadSipFileChangesDetails,
} from "../../constants/actions";
import config from "@config";
import type { Action } from "redux-action";
import { downloadXslsFile } from "@sagas/utils";
import actionCreators from "@modules/data/actionCreator";

export function* onGetFileChangesDetails(action: {
  payload: FilesRequestPayload,
}): Generator<void, void, void> {
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${action.payload.clientId}/files/${action.payload.fileId}/changes/groups/${action.payload.groupId}/details`,
    );

    yield put(actionCreator.GetFileChangesDetailsSuccess((response || {}).data));
    yield put(actionCreator.OpenFileChangesDetailsModal(true));
  } catch (e) {
    yield put(actionCreator.GetFileChangesDetailsError(e.response));
  }
}

export function* onGetDataFileArchiveSummary(action: {
  payload: FilesRequestPayload,
}): Generator<void, void, void> {
  try {
    const response = yield call(
      http.get,
      `${config.endpointBaseUrl}api/v1/data-admin/client/${action.payload.clientId}/files/${action.payload.fileId}/changes/groups`,
    );

    yield put(actionCreator.GetDataFileArchiveSummarySuccess((response || {}).data));
    yield put(actionCreator.OpenFileChangesSummaryModal(true));
  } catch (e) {
    yield put(actionCreator.GetDataFileArchiveSummaryError(e.response));
  }
}

export function* onGetDataFileArchive(action: {
  payload: FilesRequestPayload,
}): Generator<void, void, *> {
  try {
    const { clientId, search = {}, pageable = {}, sort = {} } = action.payload;
    const rewrites = {
      from: {
        name: "created_at",
        operator: OPERATORS.GREATER_THAN,
      },
      to: {
        name: "created_at",
        operator: OPERATORS.LESS_THAN,
      },
    };
    if (sort.direction) {
      pageable.sort = sortObjToStr(sort);
    }

    const response = yield call(http.get, `/api/v1/data-admin/client/${clientId}/files/details`, {
      params: {
        search: `status:CANCELLED,status:PROCESSED,${transformSearchQuery(search, rewrites)}`,
        limit: pageable ? pageable.limit : 0,
        page: pageable ? pageable.page : 0,
        sort: pageable ? pageable.sort : "",
      },
    });

    yield put(
      actionCreator.GetDataFileArchiveSuccess({
        files: response.data.content,
        pagination: response.data.pagination,
      }),
    );
  } catch (e) {
    yield put(actionCreator.GetDataFileArchiveError(e.response));
  }
}

export type DownloadDataSipFileRequest = $Exact<{
  clientId: number,
  departmentId: number,
  fileId: number,
}>;

export function* onDownloadDataSipFile({
  payload,
}: Action<DownloadDataSipFileRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.fileId || !payload.departmentId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/files/${payload.fileId}`,
      {
        responseType: "blob",
      },
    );

    downloadXslsFile(
      response.data,
      `${payload.fileId} Client ${payload.clientId}, Department ${payload.departmentId} SIP File export.xlsx`,
    );
    yield put(actionCreators.DownloadDataSipFileSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.DownloadDataSipFileError(e.response));
  }
}

export function* onDownloadSipFileChangesDetails({
  payload,
}: Action<{
  clientId: number,
  fileId: number,
  fileType: string,
  groupId: number,
}>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId || !payload.fileId || !payload.fileType || !payload.groupId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `api/v1/data-admin/client/${payload.clientId}/files/${payload.fileId}/changes/groups/${payload.groupId}/details/file`,
      {
        responseType: "blob",
      },
    );

    downloadXslsFile(
      response.data,
      `Client ${payload.clientId} ${payload.fileType} file changes export.xlsx`,
    );
    yield put(actionCreators.DownloadSipFileChangesDetailsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.DownloadSipFileChangesDetailsError(e.response));
  }
}

export default function*(): Generator<*, void, void> {
  yield takeLatest(GetFileChangesDetails, onGetFileChangesDetails);
  yield takeLatest(GetDataFileArchiveSummary, onGetDataFileArchiveSummary);
  yield takeLatest(GetDataFileArchive, onGetDataFileArchive);
  yield takeLatest(DownloadDataSipFile, onDownloadDataSipFile);
  yield takeLatest(DownloadSipFileChangesDetails, onDownloadSipFileChangesDetails);
}
